import { InfoOutlined } from '@mui/icons-material';
import { Link, Stack, Tooltip, Typography } from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/button';
import Dialog from '@/components/dialog';
import { useRescrapePosts, useUpdateYTPosts } from '@/hooks/queries/posts';
import useAlert from '@/hooks/useAlert';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Updates } from '@/models/updates';
import { formatDate } from '@/utils/datesAndTime/datesAndTime';

import DialogContent from './_partials/DialogContent';
import { UpdatesConfig } from './config/config';

interface DialogProps<T> {
  isOpen: boolean;
  context: Updates.Dialog;
  updates?: T;
  onClose: () => void;
  config: UpdatesConfig<T>;
}

interface UploadDates {
  uploadDate?: string;
  createTime?: string;
  platform?: string;
  isExpired?: boolean;
}

export default function UpdatesDialog<T extends UploadDates>({
  isOpen,
  context,
  updates,
  onClose,
  config
}: DialogProps<T>) {
  const navigate = useWorkspaceNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();

  const { mutate: rescrapePosts, isPending: isCreating } = useRescrapePosts(
    context?.postId as number,
    () => {
      queryClient.invalidateQueries({
        queryKey: config.queryKeys.list
      });
      queryClient.invalidateQueries({
        queryKey: config.queryKeys.detail(context?.postId)
      });
    },
    (e) => {
      const message = e?.response?.data?.message || 'Unexpected error occurred';
      alert.error(`Oops, something went wrong: ${message}`);
    }
  );

  const { mutate: updateYTPosts, isPending: isCreatingYT } = useUpdateYTPosts(
    context?.postId as number,
    () => {
      queryClient.invalidateQueries({
        queryKey: config.queryKeys.list
      });
      queryClient.invalidateQueries({
        queryKey: config.queryKeys.detail(context?.postId)
      });
    },
    (e) => {
      const message = e?.response?.data?.message || 'Unexpected error occurred';
      alert.error(`Oops, something went wrong: ${message}`);
    }
  );

  const isYouTubePlatform = updates?.platform
    ?.toLowerCase()
    .includes('youtube');

  const handleRescrape = () => {
    if (isYouTubePlatform) {
      updateYTPosts();
    } else {
      rescrapePosts();
    }
  };

  const rawLastUpdatedAt = updates ? config.getLastUpdatedAt(updates) : null;
  const rawOriginalRescrape = updates
    ? config.getOriginalRescrape(updates)
    : null;
  const rawDate =
    context?.updates?.length === 0 ? rawOriginalRescrape : rawLastUpdatedAt;
  const lastUpdatedAt = formatDate(rawDate, 'dd/MM/yy HH:mm');

  const hasPendingRequest = updates
    ? config.getRescrapeRequests(updates).some((req) => !req.isDone)
    : false;

  const updateExpired = updates?.isExpired;

  const getButtonText = () => {
    if (updateExpired) return 'Post Expired';
    if (hasPendingRequest) return 'Checking for changes';
    return 'Request update';
  };

  const tooltipText = (
    <>
      This allows you to check whether <strong>{context?.creatorName}</strong>{' '}
      has changed or removed their post. When checking for changes, one update
      can take up to 24 hours.
    </>
  );

  const uploadDate = updates
    ? updates?.uploadDate || updates?.createTime
    : undefined;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Changes"
      fullWidth
      maxWidth="lg"
      showCloseIcon
      showHeaderDivider
      dialogContent={
        <DialogContent
          updates={context?.updates}
          creator={context?.creatorName}
          uploadDate={uploadDate}
          isExpired={updateExpired}
        />
      }
      actions={
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Link
            underline="hover"
            sx={{ cursor: 'pointer', color: 'primary' }}
            onClick={(e) => {
              e.preventDefault();
              navigate(config.getNavigationRoute(context));
            }}
            color="primary"
          >
            <Typography>{config.linkText}</Typography>
          </Link>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
              Last Update: <strong>{lastUpdatedAt}</strong>
            </Typography>
            <Tooltip
              arrow
              placement="top"
              disableInteractive
              title={tooltipText}
            >
              <InfoOutlined fontSize="small" />
            </Tooltip>
            <Button
              onClick={handleRescrape}
              text={getButtonText()}
              disabled={hasPendingRequest || updateExpired}
              loading={isCreating || isCreatingYT}
            />
          </Stack>
        </Stack>
      }
    />
  );
}
